/**
 * Phase 3.74 — Portal-Transaktion: zusätzliche Fahrzeugdaten.
 *
 * Erscheint NUR wenn die Transaktion über das Portal läuft, d.h.:
 *  - sale_type === "auction" (Auktionen)
 *  - direct_purchase === true (Direktkauf über Plattform / „Sofort kaufen")
 * Bei reinem Börsenverkauf (B2C↔B2B Direktverkauf außerhalb des Portals) blendet
 * dieser Block aus.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { FileText, Archive, Landmark, Send, Hash, Layers, AlertTriangle, Check, Loader2, ScanLine, Printer, Wand2, ClipboardCheck, Info, ChevronDown } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { generateLeitzahl } from "../../lib/keyLeitzahl";
import DispositionActions from "./DispositionActions";
import DocumentArchiveSection from "./DocumentArchiveSection";
import BoniStatusCard from "./BoniStatusCard";
import DispoRowNote from "./DispoRowNote";
import Zb1ExtendedFieldsSection from "./sections/Zb1ExtendedFieldsSection";
import DispoLocationNote from "./DispoLocationNote";
import InfoHint from "./InfoHint";

// Feb-2026 (User-Wunsch) — alle Freitext-/Eingabefelder der Disposition werden
// automatisch gespeichert (debounced). Whitelist deckt sich mit dem Backend
// (DISPO_EDITABLE_FIELDS in dealer_dispo_router.py).
const DISPO_AUTOSAVE_KEYS = [
    "vin", "dispo_order_code", "deregistered", "deregistered_date",
    "has_coc", "has_coc_date", "has_zb1", "has_zb1_date", "has_zb1_number", "has_zb2", "has_zb2_date", "has_zb2_number",
    "papers_in_house", "papers_in_house_location", "papers_in_house_note",
    "papers_refinanced", "papers_refinanced_bank", "papers_refinanced_date", "papers_refinanced_note",
    "papers_shipped_to_buyer", "papers_shipped_date",
    "key_count", "leitzahl", "key_depot", "stand_platz", "license_plate",
    "has_immobilizer_card", "has_keyless_transponder", "has_handsender",
    "vorfuehr_usage", "vorfuehr_employee_id", "vorfuehr_employee_name",
    "vorfuehr_workshop_site_code", "vorfuehr_workshop_site_name", "vorfuehr_workshop_dealer_id", "vorfuehr_other_text",
];

// Jun-2026 — Modul-Cache, damit die Mitarbeiter-/Standort-Listen ein Remount der
// Disposition-Sektion (durch Form-Updates) überleben und die Dropdowns nicht leeren.
const _vfCache = { employees: null, sites: null };

export default function PortalTransactionSection({ form, update, listingId, hideActions = false }) {
    // Feb-2026 (User-Wunsch) — `portalVisible`-Sperre entfernt: Papier-Lagerort &
    // Dokumente sind jetzt immer sichtbar.
    // Feb-2026 (User-Wunsch) — Hersteller-Bestellcode nur bei Neufahrzeug / Tageszulassung.
    const showOrderCode = form.condition === "Neufahrzeug" || form.condition === "Tageszulassung";
    // Jun-2026 (User-Wunsch) — Bei zugelassenem Vorführfahrzeug zusätzliche
    // Nutzungs-Angabe (Dienstwagen / Miet-/Werkstattfahrzeug / Anderes).
    const showVorfuehrUsage = !form.deregistered && form.condition === "Vorführfahrzeug";
    const [vfEmployees, setVfEmployees] = useState(_vfCache.employees || []);
    const [vfSites, setVfSites] = useState(_vfCache.sites || []);
    // Aug-2026 (User-Wunsch) — Interner Vermerk startet IMMER eingeklappt.
    const [noteOpen, setNoteOpen] = useState(false);
    // Mitarbeiterliste lazy laden, sobald „Dienstwagen" gewählt wird.
    useEffect(() => {
        if (form.vorfuehr_usage === "dienstwagen" && !_vfCache.employees) {
            api.get("/dealer/employees").then((r) => { _vfCache.employees = r.data?.items || []; setVfEmployees(_vfCache.employees); }).catch(() => {});
        }
    }, [form.vorfuehr_usage]);
    // Jun-2026 (User-Wunsch / Fix) — Werkstatt-Standorte EAGER vorladen, sobald ein
    // Vorführfahrzeug angezeigt wird (nicht erst beim Klick auf „Miet-/Werkstatt-
    // fahrzeug"). So ist das Dropdown bereit, bevor der Nutzer es öffnet, und zeigt
    // nie fälschlich „Keine Standorte hinterlegt", auch wenn der Request unter dem
    // HTTP/1.1-Verbindungslimit kurz wartet. Robust: workshop-locations zuerst,
    // Fallback auf /users/me/dealer-sites. Modul-Cache überlebt Remounts.
    useEffect(() => {
        if (!showVorfuehrUsage) return;
        if (_vfCache.sites && _vfCache.sites.length) { setVfSites(_vfCache.sites); return; }
        let cancelled = false;
        const loadSites = async () => {
            let sites = [];
            try {
                const r = await api.get("/dealer/workshop-locations");
                sites = r.data?.sites || [];
            } catch { /* Fallback unten */ }
            if (!sites.length) {
                try {
                    const r2 = await api.get("/users/me/dealer-sites");
                    sites = (r2.data?.items || []).map((s) => ({
                        code: s.code, name: s.name, city: s.city,
                        dealer_id: "", dealer_name: "", scope: "own",
                    }));
                } catch { /* leer lassen */ }
            }
            if (cancelled) return;
            if (sites.length) _vfCache.sites = sites;
            setVfSites(sites);
        };
        loadSites();
        return () => { cancelled = true; };
    }, [showVorfuehrUsage]);
    // Jun-2026 (User-Wunsch) — Standort des Fahrzeugs als Default vorschlagen;
    // passt der site_code nicht (oder fehlt), den ersten eigenen Standort wählen.
    useEffect(() => {
        if (form.vorfuehr_usage !== "miet_werkstatt") return;
        if (form.vorfuehr_workshop_site_code) return; // bereits gewählt
        if (!vfSites.length) return;
        const own = vfSites.find((s) => (s.scope === "own" || !s.dealer_id) && s.code === form.site_code)
            || vfSites.find((s) => s.scope === "own" || !s.dealer_id)
            || vfSites[0];
        if (own) {
            update("vorfuehr_workshop_site_code", own.code || "");
            update("vorfuehr_workshop_site_name", `${own.name || own.city || own.code}${own.scope && own.scope !== "own" ? ` · ${own.dealer_name}` : ""}`);
            if (own.dealer_id) update("vorfuehr_workshop_dealer_id", own.dealer_id);
        }
    }, [form.vorfuehr_usage, vfSites]);
    const empLabel = (e) => e.name || [e.first_name, e.last_name].filter(Boolean).join(" ") || e.email || "Mitarbeiter";
    // Feb-2026 (User-Wunsch) — Live-Zähler „X baugleiche Fahrzeuge in dieser
    // Modellreihe" direkt neben dem Bestellcode-Feld (debounced, schon beim Tippen).
    const [seriesCount, setSeriesCount] = useState(null);
    useEffect(() => {
        const code = (form.dispo_order_code || "").trim();
        if (!showOrderCode || !code) { setSeriesCount(null); return; }
        const ctrl = new AbortController();
        const tmr = setTimeout(async () => {
            try {
                const { data } = await api.get("/listings/dispo/order-code-count", {
                    params: { order_code: code, condition: form.condition },
                    signal: ctrl.signal,
                });
                if (data?.applicable) setSeriesCount(data.count);
                else setSeriesCount(null);
            } catch { /* abgebrochen / Fehler — Zähler ausblenden */ }
        }, 450);
        return () => { clearTimeout(tmr); ctrl.abort(); };
    }, [form.dispo_order_code, form.condition, showOrderCode]);


    // Feb-2026 (User-Wunsch) — Beim Anhaken eines Papierfeldes automatisch das
    // heutige Datum hinterlegen (nur falls noch leer). Beim Entfernen des Hakens
    // bleibt das Datum erhalten, wird aber nicht mehr angezeigt.
    const todayISO = () => new Date().toISOString().slice(0, 10);
    const togglePaper = (flagKey, dateKey, checked) => {
        update(flagKey, checked);
        if (checked && !form[dateKey]) update(dateKey, todayISO());
    };

    // Jun-2026 (User-Wunsch) — Schlüssel-Leitzahl automatisch erzeugen
    // (letzte 2 FIN-Ziffern + 2 Buchstaben des Straßennamens), manuell
    // überschreibbar. Jul-2026: mit „Schlüsseldepot" zu EINEM Feld (key_depot)
    // zusammengefasst — Auto-Befüllung nur, solange das Feld leer ist.
    const leitzahlAuto = useRef(false);
    useEffect(() => {
        if (leitzahlAuto.current) return;
        if ((form.key_depot || "").trim() || (form.leitzahl || "").trim()) { leitzahlAuto.current = true; return; }
        const gen = generateLeitzahl({ vin: form.vin, street: form.street || form.location });
        if (gen) { update("key_depot", gen); leitzahlAuto.current = true; }
    }, [form.vin, form.street, form.location, form.key_depot, form.leitzahl, update]);
    const fillLeitzahl = () => {
        const gen = generateLeitzahl({ vin: form.vin, street: form.street || form.location });
        if (!gen) { toast.error("FIN oder Straße fehlt für das Auto-Schlüsseldepot"); return; }
        update("key_depot", gen);
    };

    // Jun-2026 (User-Wunsch) — Fahrzeugpapierinventur (Code auf den Papieren).
    const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };
    const [papersCode, setPapersCode] = useState(form.papers_inventory_code || "");
    const [papersScan, setPapersScan] = useState("");
    const [papersLastAt, setPapersLastAt] = useState(form.papers_inventory_last_counted_at || "");
    const [papersBusy, setPapersBusy] = useState(false);
    const papersLoaded = useRef(false);
    useEffect(() => {
        if (!listingId || papersLoaded.current) return;
        papersLoaded.current = true;
        api.get(`/listings/${listingId}`)
            .then((r) => { setPapersCode(r.data?.papers_inventory_code || ""); setPapersLastAt(r.data?.papers_inventory_last_counted_at || ""); })
            .catch(() => {});
    }, [listingId]);
    const openPapersLabel = useCallback(async () => {
        try {
            const r = await api.get(`/listings/${listingId}/dispo/papers-inventory/label.pdf`, { responseType: "blob" });
            previewPdfResponse(r, "Papier-Etikett.pdf");
        } catch (e) { toast.error(e.response?.data?.detail || "Etikett konnte nicht geladen werden"); }
    }, [listingId]);
    const scanPapers = async () => {
        const code = (papersScan || papersCode).trim();
        if (!code) { toast.error("Bitte Papiercode scannen / eingeben"); return; }
        setPapersBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/papers-inventory/scan`, { code });
            setPapersCode(r.data.code); setPapersLastAt(r.data.last_counted_at); setPapersScan("");
            toast.success(r.data.action === "registered" ? "Papiercode erfasst ✓" : "Papierzählung gebucht ✓");
        } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
        finally { setPapersBusy(false); }
    };
    const generatePapers = async () => {
        setPapersBusy(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/papers-inventory/generate`, {});
            setPapersCode(r.data.code);
            toast.success("Code aus FIN erstellt ✓ — Etikett wird geöffnet");
            await openPapersLabel();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
        finally { setPapersBusy(false); }
    };

    // Jun-2026 (User-Wunsch) — Papier-Lagerort jetzt EINFACHAUSWAHL: „Im Hause",
    // „Refinanz." und „Versendet" schließen sich gegenseitig aus (Radio-Verhalten).
    // Auswahl einer Option deaktiviert die beiden anderen automatisch; erneuter
    // Klick auf die aktive Option hebt die Auswahl wieder auf.
    const PAPER_FLAGS = ["papers_in_house", "papers_refinanced", "papers_shipped_to_buyer"];
    const PAPER_DATE_KEY = { papers_refinanced: "papers_refinanced_date", papers_shipped_to_buyer: "papers_shipped_date" };
    const selectPaper = (flagKey) => {
        const willActivate = !form[flagKey];
        PAPER_FLAGS.forEach((k) => update(k, k === flagKey ? willActivate : false));
        const dateKey = PAPER_DATE_KEY[flagKey];
        if (willActivate && dateKey && !form[dateKey]) update(dateKey, todayISO());
    };

    // Jun-2026 (User-Wunsch) — strukturierter Papier-Lagerort (Ordner A–Z + Hüllen).
    // Zeigt die ersten freien Lagerplätze in Ordner-Reihenfolge; ein Platz wird
    // frei, sobald die Papiere versendet sind.
    const [freeSlots, setFreeSlots] = useState({ slots: [], folder: null, free_count: 0, total: 0 });
    const loadFreeSlots = useCallback(async () => {
        if (!listingId) return;
        try {
            const r = await api.get("/dealer/paper-storage/free-slots", { params: { limit: 5, exclude_listing: listingId } });
            setFreeSlots(r.data || {});
        } catch { /* noop */ }
    }, [listingId]);
    useEffect(() => { if (form.papers_in_house) loadFreeSlots(); }, [form.papers_in_house, loadFreeSlots]);
    const pickSlot = (slot) => {
        update("papers_storage_slot", form.papers_storage_slot === slot ? "" : slot);
        setTimeout(loadFreeSlots, 300);
    };
    // Mitarbeiterliste für „Angekauft von" (Nachrecherche-Zuweisung).
    const [employees, setEmployees] = useState([]);
    useEffect(() => {
        let alive = true;
        api.get("/me/tasks/assignable-employees")
            .then((r) => { if (alive) setEmployees(r.data?.items || []); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    const setAnkaufEmployee = (id) => {
        const emp = employees.find((e) => e.id === id);
        update("ankauf_employee_id", id || "");
        update("ankauf_employee_name", emp ? emp.name : "");
    };

    // Feb-2026 (User-Wunsch) — Auto-Speichern aller Disposition-Felder.
    // Beobachtet die Whitelist-Felder im Formular und persistiert Änderungen
    // debounced über PATCH /dispo/fields. Der große „Speichern"-Button bleibt
    // als zusätzliche Absicherung erhalten.
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
    const prevSnapRef = useRef(null);
    const pendingRef = useRef({});
    const timerRef = useRef(null);
    const savedTimerRef = useRef(null);

    const flushDispoFields = useCallback(async () => {
        const payload = pendingRef.current;
        pendingRef.current = {};
        if (!listingId || Object.keys(payload).length === 0) return;
        setSaveState("saving");
        try {
            await api.patch(`/listings/${listingId}/dispo/fields`, payload);
            setSaveState("saved");
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(() => setSaveState("idle"), 1800);
        } catch {
            setSaveState("idle");
        }
    }, [listingId]);

    useEffect(() => {
        if (!listingId) return;
        const snap = {};
        DISPO_AUTOSAVE_KEYS.forEach((k) => { snap[k] = form[k]; });
        if (prevSnapRef.current === null) { prevSnapRef.current = snap; return; }
        let changed = false;
        DISPO_AUTOSAVE_KEYS.forEach((k) => {
            if (JSON.stringify(snap[k]) !== JSON.stringify(prevSnapRef.current[k])) {
                pendingRef.current[k] = snap[k] ?? null;
                changed = true;
            }
        });
        prevSnapRef.current = snap;
        if (!changed) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flushDispoFields, 800);
    }, [form, listingId, flushDispoFields]);

    // Beim Verlassen der Sektion ausstehende Änderungen sofort sichern.
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        flushDispoFields();
    }, [flushDispoFields]);


    return (
        <div className="relative bg-white border-2 border-emerald-200 rounded-sm p-6" data-testid="portal-transaction-section">
            {/* Feb-2026 (User-Wunsch) — Auto-Speichern-Status (alle Disposition-Felder
                werden automatisch gespeichert). */}
            {listingId && saveState !== "idle" && (
                <span
                    className={`absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-sm ${saveState === "saving" ? "bg-gray-100 text-gray-500" : "bg-emerald-50 text-emerald-700"}`}
                    data-testid="dispo-autosave-indicator"
                >
                    {saveState === "saving"
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Speichert…</>
                        : <><Check className="w-3 h-3" /> Automatisch gespeichert</>}
                </span>
            )}
            {/* Feb-2026 (User-Wunsch 24 May Iter 14): doppelte „Disposition / Portal-
                Transaktion"-Überschrift entfernt — Outer SectionToggle trägt den
                Titel bereits. */}

            {/* Feb-2026 (User-Wunsch) — Angebots-/Fahrzeugnummer als ALLEROBERSTES
                Element der Disposition (unveränderlich, für QR/Transport/Belege). */}
            {form.internal_number && (
                <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="dispo-internal-number-top">
                    <span
                        className="font-mono-num text-sm font-bold px-3 py-1.5 rounded-sm bg-[#111827] text-[#FFCC00] tracking-wider"
                        title="Einmalige und unveränderliche Angebots-/Fahrzeugnummer"
                    >
                        Nr. {form.internal_number}
                    </span>
                    {form.sold && (
                        <span
                            className="inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-wider px-3 py-1.5 rounded-sm bg-red-600 text-white"
                            data-testid="dispo-sold-banner"
                            title="Dieses Fahrzeug ist als verkauft gekennzeichnet"
                        >
                            <Check className="w-4 h-4" /> Verkauft
                        </span>
                    )}
                </div>
            )}

            {/* Feb-2026 (User-Wunsch) — VIN-Bereich GANZ OBEN, über dem Zulassungsstatus:
                Links VIN + Hersteller-Bestellcode (immer editierbar, direkt unter
                der VIN). Rechts Vorbesitzer (eingeklappt) + kompakte Dispo-Info. */}
            <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-100"
                data-testid="vin-area-row"
            >
                {/* Links: VIN + Hersteller-Bestellcode */}
                <div className="space-y-3">
                    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="vin-always-visible-block">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                            Fahrgestellnummer (VIN)
                            <InfoHint id="dispo.vin" defaultText="17 Zeichen · wird automatisch in Großbuchstaben gespeichert." />
                        </Label>
                        <Input
                            value={form.vin || ""}
                            onChange={(e) => update("vin", e.target.value.toUpperCase())}
                            placeholder="z. B. WVWZZZ6RZAW012345"
                            maxLength={17}
                            className="mt-1.5 rounded-sm font-mono-num uppercase"
                            data-testid="form-vin"
                        />
                        {(form.vin || "").trim().length > 0 && (form.vin || "").trim().length < 17 && (
                            <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-700" data-testid="vin-length-warning">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Unvollständig: {(form.vin || "").trim().length}/17 Zeichen
                            </div>
                        )}
                    </div>

                    {/* Feb-2026 (User-Wunsch) — Hersteller-Bestellcode IMMER editierbar
                        (auch bei Gebrauchtfahrzeug), direkt unter der Fahrgestellnummer. */}
                    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="order-code-block">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1.5">
                            <Hash className="w-3.5 h-3.5" /> Hersteller-Bestellcode (Modellreihe)
                            <InfoHint id="dispo.order_code" defaultText="Gruppiert baugleiche Fahrzeuge zur Modellreihe (Neufahrzeug / Tageszulassung)." />
                        </Label>
                        <Input
                            value={form.dispo_order_code || ""}
                            onChange={(e) => update("dispo_order_code", e.target.value)}
                            placeholder="z. B. Modellcode / Bestellnummer"
                            maxLength={120}
                            className="mt-1.5 rounded-sm font-mono-num"
                            data-testid="form-dispo-order-code"
                        />
                        {seriesCount != null && (form.dispo_order_code || "").trim() && (
                            <div
                                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold bg-[#111827] text-[#FFCC00] px-2 py-1 rounded-sm"
                                data-testid="order-code-series-count"
                            >
                                <Layers className="w-3.5 h-3.5" />
                                {seriesCount === 0
                                    ? "Neue Modellreihe — noch kein baugleiches Fahrzeug"
                                    : `${seriesCount} baugleiche${seriesCount === 1 ? "s" : ""} Fahrzeug${seriesCount === 1 ? "" : "e"} in dieser Modellreihe`}
                            </div>
                        )}
                    </div>

                    {/* Feb-2026 (User-Wunsch) — Zulassungsstatus (Aktuell zugelassen /
                        Letzte Abmeldung) kompakt direkt unter dem Hersteller-Bestellcode
                        (Modellreihe). KI setzt `form.deregistered` automatisch, wenn ein
                        Stempel „Außerbetriebsetzung" / „abgemeldet am …" erkannt wird.
                        Manuelles Toggle bleibt möglich. */}
                    <div
                        className={`rounded-sm p-3 ${form.deregistered ? "bg-red-50 border border-red-200" : "bg-gray-50 border border-gray-200"}`}
                        data-testid="disposition-deregistered-block"
                    >
                        <div className="flex items-center gap-x-3 flex-nowrap overflow-x-auto" data-testid="zulassungsstatus-row">
                            <div className="flex items-center gap-1.5 shrink-0">
                                <Archive className={`w-4 h-4 ${form.deregistered ? "text-red-700" : "text-gray-500"}`} />
                                <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Zulassungsstatus:</Label>
                            </div>
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer shrink-0">
                                <input
                                    type="checkbox"
                                    checked={!!form.deregistered}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        update("deregistered", checked);
                                        // Jun-2026 (User-Wunsch) — beim Klick auf „Abmeldung"
                                        // automatisch das heutige Datum setzen (sofern leer);
                                        // beim Abwählen (Wiederzulassung) wieder leeren. Die
                                        // Änderung wird beim Speichern automatisch ins
                                        // Fahrzeug-Protokoll (audit_log) geschrieben.
                                        if (checked) {
                                            if (!form.deregistered_date) update("deregistered_date", todayISO());
                                        } else {
                                            update("deregistered_date", "");
                                        }
                                    }}
                                    className="accent-red-600"
                                    data-testid="form-deregistered-toggle"
                                />
                                <span className={`font-bold ${form.deregistered ? "text-red-700" : "text-emerald-700"}`}>
                                    {form.deregistered ? "Abgemeldet / außer Betrieb" : "Aktuell zugelassen"}
                                </span>
                            </label>
                            <div className="flex items-center gap-1.5 shrink-0" data-testid="deregistration-date-inline">
                                <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600 shrink-0">{form.deregistered ? "Abmeldedatum" : "Abmeldung"}</Label>
                                <input
                                    type="date"
                                    value={form.deregistered_date || ""}
                                    onChange={(e) => update("deregistered_date", e.target.value)}
                                    className="h-8 text-sm border border-gray-300 rounded-sm px-2 bg-white"
                                    data-testid="form-deregistered-date"
                                    title="Datum der (letzten) bekannten Abmeldung — relevant für Versicherung / Steuer"
                                />
                            </div>
                            {/* Jun-2026 (User-Wunsch) — Kennzeichen direkt im Zulassungsstatus. */}
                            <div className="flex items-center gap-1.5 shrink-0" data-testid="license-plate-inline">
                                <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600 shrink-0">Kennzeichen</Label>
                                <input
                                    type="text"
                                    maxLength={20}
                                    value={form.license_plate || ""}
                                    onChange={(e) => update("license_plate", e.target.value.toUpperCase().slice(0, 20))}
                                    placeholder="z. B. M-MG 59"
                                    className="h-8 text-sm border border-gray-300 rounded-sm px-2 bg-white font-mono-num uppercase w-32"
                                    data-testid="form-license-plate"
                                    title="Amtliches Kennzeichen des Fahrzeugs"
                                />
                            </div>
                        </div>
                        {form.deregistered && (
                            <p className="text-[11px] text-red-700 mt-2 leading-snug">
                                ⚠ Dieses Fahrzeug ist außer Betrieb gesetzt. Käufer müssen vor Probefahrt eine Kurzzeit-/Überführungs-Zulassung organisieren.
                            </p>
                        )}
                        {/* Jun-2026 (User-Wunsch) — Zugelassenes Vorführfahrzeug: aktuelle Nutzung */}
                        {showVorfuehrUsage && (
                            <div className="mt-3 border-t border-gray-200 pt-3 space-y-2" data-testid="vorfuehr-usage-block">
                                <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Vorführfahrzeug — aktuelle Nutzung</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    {[
                                        { key: "dienstwagen", label: "Wird als Dienstwagen benutzt" },
                                        { key: "miet_werkstatt", label: "Als Miet- / Werkstattfahrzeug" },
                                        { key: "anderes", label: "Anderes" },
                                    ].map((opt) => {
                                        const active = form.vorfuehr_usage === opt.key;
                                        return (
                                            <label
                                                key={opt.key}
                                                className={`flex items-center gap-2 text-xs cursor-pointer rounded-sm border px-2.5 py-2 transition-colors ${active ? "border-[#111827] bg-[#FFCC00]/15" : "border-gray-200 hover:border-gray-400"}`}
                                                data-testid={`vorfuehr-usage-${opt.key}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={active}
                                                    onChange={() => update("vorfuehr_usage", active ? "" : opt.key)}
                                                    className="accent-[#111827]"
                                                    data-testid={`vorfuehr-usage-${opt.key}-input`}
                                                />
                                                <span className="font-medium">{opt.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {form.vorfuehr_usage === "dienstwagen" && (
                                    <div data-testid="vorfuehr-employee-wrap">
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Mitarbeiter auswählen</Label>
                                        <select
                                            value={form.vorfuehr_employee_id || ""}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                const emp = vfEmployees.find((x) => x.id === id);
                                                update("vorfuehr_employee_id", id);
                                                update("vorfuehr_employee_name", emp ? empLabel(emp) : "");
                                            }}
                                            className="mt-1 h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white w-full"
                                            data-testid="vorfuehr-employee-select"
                                        >
                                            <option value="">— Mitarbeiter wählen —</option>
                                            {vfEmployees.map((emp) => (
                                                <option key={emp.id} value={emp.id}>{empLabel(emp)}</option>
                                            ))}
                                        </select>
                                        {vfEmployees.length === 0 && (
                                            <p className="text-[11px] text-gray-500 mt-1">Keine Mitarbeiter hinterlegt — unter „Mitarbeiter“ anlegen.</p>
                                        )}
                                    </div>
                                )}
                                {form.vorfuehr_usage === "miet_werkstatt" && (
                                    <div data-testid="vorfuehr-workshop-wrap">
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Standort der Werkstatt</Label>
                                        <select
                                            value={`${form.vorfuehr_workshop_dealer_id || ""}|${form.vorfuehr_workshop_site_code || ""}`}
                                            onChange={(e) => {
                                                const [did, code] = e.target.value.split("|");
                                                const site = vfSites.find((x) => x.code === code && (x.dealer_id || "") === (did || ""));
                                                update("vorfuehr_workshop_site_code", code || "");
                                                update("vorfuehr_workshop_dealer_id", did || "");
                                                update("vorfuehr_workshop_site_name", site ? `${site.name || site.city || code}${site.scope && site.scope !== "own" ? ` · ${site.dealer_name}` : ""}` : "");
                                            }}
                                            className="mt-1 h-9 text-sm border border-gray-300 rounded-sm px-2 bg-white w-full"
                                            data-testid="vorfuehr-workshop-select"
                                        >
                                            <option value="|">— Standort wählen —</option>
                                            {vfSites.map((s) => {
                                                const hasCnt = typeof s.rental_total === "number" && s.rental_total > 0;
                                                const cnt = hasCnt ? `  ·  ${s.rental_available} frei / ${s.rental_assigned} vergeben` : "";
                                                return (
                                                    <option key={`${s.dealer_id || ""}|${s.code}`} value={`${s.dealer_id || ""}|${s.code}`}>
                                                        {s.name}{s.city ? ` · ${s.city}` : ""}{s.scope && s.scope !== "own" ? ` — ${s.dealer_name} (verknüpft)` : ""}{cnt}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        {(() => {
                                            const sel = vfSites.find((x) => x.code === form.vorfuehr_workshop_site_code && (x.dealer_id || "") === (form.vorfuehr_workshop_dealer_id || ""));
                                            if (sel && typeof sel.rental_total === "number" && sel.rental_total > 0) {
                                                return (
                                                    <p className="text-[11px] mt-1 flex items-center gap-2" data-testid="vorfuehr-workshop-utilization">
                                                        <span className="font-bold text-emerald-700">{sel.rental_available} frei</span>
                                                        <span className="text-gray-300">|</span>
                                                        <span className="font-bold text-amber-700">{sel.rental_assigned} vergeben</span>
                                                        <span className="text-gray-400">von {sel.rental_total} Ersatzfahrzeugen an diesem Standort</span>
                                                    </p>
                                                );
                                            }
                                            return null;
                                        })()}
                                        {vfSites.length === 0 && (
                                            <p className="text-[11px] text-gray-500 mt-1">Keine Standorte hinterlegt — unter „Standorte“ anlegen.</p>
                                        )}
                                    </div>
                                )}
                                {form.vorfuehr_usage === "anderes" && (
                                    <div data-testid="vorfuehr-other-wrap">
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Beschreibung</Label>
                                        <textarea
                                            value={form.vorfuehr_other_text || ""}
                                            onChange={(e) => update("vorfuehr_other_text", e.target.value)}
                                            rows={2}
                                            maxLength={500}
                                            placeholder="Freie Eingabe…"
                                            className="mt-1 text-sm border border-gray-300 rounded-sm px-2 py-1.5 bg-white w-full"
                                            data-testid="vorfuehr-other-text"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Feb-2026 (User-Wunsch) — KI-Dokumentenarchiv links, unter dem
                        Zulassungsstatus. */}
                    {listingId && <DocumentArchiveSection listingId={listingId} form={form} update={update} />}
                    {/* Jun-2026 (User-Wunsch) — Interner Vermerk hierher verschoben
                        (aus der Dispositions-Listenkarte), unter den archivierten
                        Dokumenten. Nur intern sichtbar, debounced auto-gespeichert. */}
                    {listingId && (
                        <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="dispo-internal-note-block">
                            <button
                                type="button"
                                onClick={() => setNoteOpen((v) => !v)}
                                className="w-full text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5"
                                data-testid="dispo-internal-note-toggle"
                                aria-expanded={noteOpen}
                            >
                                <FileText className="w-3.5 h-3.5 shrink-0" /> Interner Vermerk
                                <span
                                    className="text-gray-400 hover:text-[#111827] cursor-help shrink-0"
                                    title="Nur intern sichtbar · auto-gespeichert"
                                    aria-label="Nur intern sichtbar · auto-gespeichert"
                                    data-testid="dispo-internal-note-info"
                                >
                                    <Info className="w-3.5 h-3.5" />
                                </span>
                                {!noteOpen && (form.dispo_internal_note || "").trim() && (
                                    <span className="font-normal normal-case tracking-normal text-gray-400 truncate flex-1 min-w-0">
                                        — {form.dispo_internal_note}
                                    </span>
                                )}
                                <ChevronDown className={`w-3.5 h-3.5 ml-auto shrink-0 transition-transform ${noteOpen ? "rotate-180" : ""}`} />
                            </button>
                            {noteOpen && (
                                <div className="mt-1.5">
                                    <DispoRowNote
                                        listingId={listingId}
                                        initialNote={form.dispo_internal_note || ""}
                                        variant="block"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {/* Jun-2026 (User-Wunsch) — Boni-Nachverfolgung direkt unter dem
                        KI-Dokumentenarchiv (read-only Statusübersicht des Fahrzeugs). */}
                    {listingId && <BoniStatusCard listingId={listingId} />}
                </div>

                {/* Rechts: Vorbesitzer (eingeklappt) + kompakte Dispo-Info */}
                <div className="space-y-3">
                    <div data-testid="zb1-extended-wrap">
                        <Zb1ExtendedFieldsSection form={form} update={update} />
                    </div>
                    {/* Feb-2026 (User-Wunsch) — „Erhalten"-Checkliste: erhaltene
                        Papiere & Schlüssel direkt im VIN-Bereich (ersetzt die
                        frühere Kompakt-Info-Zeile). Felder zum Anhaken. */}
                    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="dispo-erhalten-block">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-gray-700" />
                            <span className="font-bold text-xs uppercase tracking-wider text-gray-700">Fahrzeugpapiere</span>
                        </div>
                        <div className="space-y-1.5">
                            {/* Feb-2026 (User-Wunsch) — COC, ZB1 und ZB2 nebeneinander
                                in EINER Zeile (3 Spalten), jeweils mit Datum-Feld. */}
                            <div className="grid grid-cols-3 gap-2">
                                <div className="border border-gray-200 rounded-sm p-2">
                                    <label className="flex items-start gap-1.5 cursor-pointer">
                                        <input type="checkbox" checked={!!form.has_coc} onChange={(e) => togglePaper("has_coc", "has_coc_date", e.target.checked)} className="w-4 h-4 accent-[#111827] mt-0.5 shrink-0" data-testid="erhalten-coc" />
                                        <span className="text-xs font-bold leading-tight inline-flex items-center gap-1">COC <InfoHint id="dispo.doc_coc" defaultText="COC — EU-Übereinstimmungsbescheinigung (Konformitätserklärung). Wird u. a. für die Erstzulassung neuer / importierter Fahrzeuge benötigt." /></span>
                                    </label>
                                    {form.has_coc && (
                                        <input type="date" value={form.has_coc_date || ""} onChange={(e) => update("has_coc_date", e.target.value)} className="mt-1.5 w-full h-7 text-xs border border-gray-300 rounded-sm px-1 font-mono-num" data-testid="erhalten-coc-date" title="Erhalten am" />
                                    )}
                                </div>
                                <div className="border border-gray-200 rounded-sm p-2">
                                    <label className="flex items-start gap-1.5 cursor-pointer">
                                        <input type="checkbox" checked={!!form.has_zb1} onChange={(e) => togglePaper("has_zb1", "has_zb1_date", e.target.checked)} className="w-4 h-4 accent-[#111827] mt-0.5 shrink-0" data-testid="erhalten-zb1" />
                                        <span className="text-xs font-bold leading-tight inline-flex items-center gap-1">ZB1 <InfoHint id="dispo.doc_zb1" defaultText="ZB1 — Zulassungsbescheinigung Teil 1 (früher Fahrzeugschein). Wird im Fahrzeug mitgeführt und für die Zulassung benötigt." /></span>
                                    </label>
                                    {form.has_zb1 && (
                                        <>
                                            <input type="date" value={form.has_zb1_date || ""} onChange={(e) => update("has_zb1_date", e.target.value)} className="mt-1.5 w-full h-7 text-xs border border-gray-300 rounded-sm px-1 font-mono-num" data-testid="erhalten-zb1-date" title="Erhalten am" />
                                            <input type="text" maxLength={12} value={form.has_zb1_number || ""} onChange={(e) => update("has_zb1_number", e.target.value.slice(0, 12))} placeholder="ZB1-Nr." className="mt-1 w-full h-7 text-xs border border-gray-300 rounded-sm px-1 font-mono-num uppercase" data-testid="erhalten-zb1-number" title="ZB1-Nummer (max. 12 Stellen)" />
                                        </>
                                    )}
                                </div>
                                <div className="border border-gray-200 rounded-sm p-2">
                                    <label className="flex items-start gap-1.5 cursor-pointer">
                                        <input type="checkbox" checked={!!form.has_zb2} onChange={(e) => togglePaper("has_zb2", "has_zb2_date", e.target.checked)} className="w-4 h-4 accent-[#111827] mt-0.5 shrink-0" data-testid="erhalten-zb2" />
                                        <span className="text-xs font-bold leading-tight inline-flex items-center gap-1">ZB2 <InfoHint id="dispo.doc_zb2" defaultText="ZB2 — Zulassungsbescheinigung Teil 2 (früher Fahrzeugbrief). Eigentumsnachweis, verbleibt i. d. R. beim Halter bzw. der finanzierenden Bank." /></span>
                                    </label>
                                    {form.has_zb2 && (
                                        <>
                                            <input type="date" value={form.has_zb2_date || ""} onChange={(e) => update("has_zb2_date", e.target.value)} className="mt-1.5 w-full h-7 text-xs border border-gray-300 rounded-sm px-1 font-mono-num" data-testid="erhalten-zb2-date" title="Erhalten am" />
                                            <input type="text" maxLength={12} value={form.has_zb2_number || ""} onChange={(e) => update("has_zb2_number", e.target.value.slice(0, 12))} placeholder="ZB2-Nr." className="mt-1 w-full h-7 text-xs border border-gray-300 rounded-sm px-1 font-mono-num uppercase" data-testid="erhalten-zb2-number" title="ZB2-Nummer (max. 12 Stellen)" />
                                        </>
                                    )}
                                </div>
                            </div>
                            {/* Feb-2026 (User-Wunsch) — Papier-Lagerort (Im Hause /
                                Refinanziert / Versendet) NEBENEINANDER, zwischen
                                COC/ZB1/ZB2 und den Schlüsseln. Detailfelder klappen
                                bei Auswahl darunter auf. */}
                            <div className="pt-1" data-testid="papers-storage-block">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Lagerort der Fahrzeugpapiere <span className="text-gray-400 normal-case font-normal">(Einfachauswahl)</span></div>
                                <div className="grid grid-cols-3 gap-1.5">
                                    <label className={`flex items-center gap-1 cursor-pointer border rounded-sm px-1.5 py-1.5 text-[11px] font-bold ${form.papers_in_house ? "border-[#111827] bg-gray-50" : "border-gray-200"}`}>
                                        <input type="radio" name="papers-storage" checked={!!form.papers_in_house} onClick={() => selectPaper("papers_in_house")} onChange={() => {}} className="w-3.5 h-3.5 accent-[#111827] shrink-0" data-testid="form-papers-in-house" />
                                        <Archive className="w-3.5 h-3.5 text-gray-700 shrink-0" /> Im Hause
                                    </label>
                                    <label className={`flex items-center gap-1 cursor-pointer border rounded-sm px-1.5 py-1.5 text-[11px] font-bold ${form.papers_refinanced ? "border-[#111827] bg-gray-50" : "border-gray-200"}`}>
                                        <input type="radio" name="papers-storage" checked={!!form.papers_refinanced} onClick={() => selectPaper("papers_refinanced")} onChange={() => {}} className="w-3.5 h-3.5 accent-[#111827] shrink-0" data-testid="form-papers-refinanced" />
                                        <Landmark className="w-3.5 h-3.5 text-gray-700 shrink-0" /> Refinanz.
                                    </label>
                                    <label className={`flex items-center gap-1 cursor-pointer border rounded-sm px-1.5 py-1.5 text-[11px] font-bold ${form.papers_shipped_to_buyer ? "border-[#111827] bg-gray-50" : "border-gray-200"}`}>
                                        <input type="radio" name="papers-storage" checked={!!form.papers_shipped_to_buyer} onClick={() => selectPaper("papers_shipped_to_buyer")} onChange={() => {}} className="w-3.5 h-3.5 accent-[#111827] shrink-0" data-testid="form-papers-shipped" />
                                        <Send className="w-3.5 h-3.5 text-gray-700 shrink-0" /> Versendet
                                    </label>
                                </div>
                                {form.papers_in_house && (
                                    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-sm p-2 space-y-2">
                                        <div data-testid="papers-slot-selector">
                                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">Lagerplatz (Ordner / Hülle) <InfoHint id="dispo.papers_storage_slot" defaultText="Strukturierter Lagerplatz aus Ordner A–Z mit Hüllen (z. B. A1). Es werden die nächsten freien Plätze in Ordner-Reihenfolge vorgeschlagen. Ein Platz wird wieder frei, sobald die Papiere versendet sind." side="bottom" /></Label>
                                            {form.papers_storage_slot ? (
                                                <div className="mt-1 flex items-center gap-2" data-testid="papers-slot-current">
                                                    <span className="inline-flex items-center gap-1 bg-[#111827] text-white font-bold text-sm rounded-sm px-2.5 py-1 font-mono-num">{form.papers_storage_slot}</span>
                                                    <button type="button" onClick={() => pickSlot(form.papers_storage_slot)} className="text-[11px] font-bold text-red-600 hover:text-red-800" data-testid="papers-slot-clear">Platz freigeben / ändern</button>
                                                </div>
                                            ) : (
                                                <div className="mt-1">
                                                    {freeSlots.slots && freeSlots.slots.length > 0 ? (
                                                        <div className="flex flex-wrap items-center gap-1.5" data-testid="papers-slot-free-list">
                                                            <span className="text-[10px] text-gray-500">Freie Plätze (Ordner {freeSlots.folder}):</span>
                                                            {freeSlots.slots.map((s) => (
                                                                <button key={s} type="button" onClick={() => pickSlot(s)} className="text-xs font-bold font-mono-num border border-gray-300 rounded-sm px-2 py-1 hover:border-[#111827] hover:bg-white" data-testid={`papers-slot-${s}`}>{s}</button>
                                                            ))}
                                                            <span className="text-[10px] text-gray-400 ml-1">{freeSlots.free_count}/{freeSlots.total} frei</span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-[11px] text-amber-700" data-testid="papers-slot-full">Keine freien Lagerplätze. Bitte Konfiguration in den Einstellungen anpassen.</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Sonderort / Ablage-Stelle <span className="text-gray-400 normal-case font-normal">(optional)</span></Label>
                                            <Input value={form.papers_in_house_location || ""} onChange={(e) => update("papers_in_house_location", e.target.value)} placeholder="z. B. Tresor Halle 2 · Ordner Schwarz" className="mt-1 h-8 rounded-sm" data-testid="form-papers-in-house-location" />
                                        </div>
                                        <div>
                                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">Angekauft von (Verkäufer) <InfoHint id="dispo.ankauf_employee" defaultText="Verkäufer/Mitarbeiter, der dieses Fahrzeug angekauft hat. Wird bei einer Papier-Nachrecherche automatisch als Zuständiger vorgeschlagen." side="bottom" /></Label>
                                            <select value={form.ankauf_employee_id || ""} onChange={(e) => setAnkaufEmployee(e.target.value)} className="mt-1 h-8 w-full rounded-sm border border-gray-300 px-2 text-sm bg-white" data-testid="form-ankauf-employee">
                                                <option value="">— nicht zugeordnet —</option>
                                                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                                            </select>
                                        </div>
                                        <textarea value={form.papers_in_house_note || ""} onChange={(e) => update("papers_in_house_note", e.target.value)} placeholder="Zusätzliche Vermerke …" rows={2} className="w-full rounded-sm border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827]" data-testid="form-papers-in-house-note" />
                                    </div>
                                )}
                                {form.papers_refinanced && (
                                    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-sm p-2 space-y-2">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div>
                                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">Finanzierende Bank <InfoHint id="dispo.papers_refinanced_bank" defaultText="Bank/Institut, das die Fahrzeugpapiere bis zur Ablösung verwahrt (Refinanzierung des Bestands). Die Papiere werden erst nach Tilgung ausgehändigt." side="bottom" /></Label>
                                                <Input value={form.papers_refinanced_bank || ""} onChange={(e) => update("papers_refinanced_bank", e.target.value)} placeholder="z. B. BMW Bank GmbH" className="mt-1 h-8 rounded-sm" data-testid="form-papers-refinanced-bank" />
                                            </div>
                                            <div>
                                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">Datum der Refinanzierung <InfoHint id="dispo.papers_refinanced_date" defaultText="Tag, an dem das Fahrzeug über die Bank refinanziert wurde. Dient als Stichtag für Zinsen/Tilgung und die Ablösefrist." side="bottom" /></Label>
                                                <Input type="date" value={form.papers_refinanced_date || ""} onChange={(e) => update("papers_refinanced_date", e.target.value)} className="mt-1 h-8 rounded-sm font-mono-num" data-testid="form-papers-refinanced-date" />
                                            </div>
                                        </div>
                                        <textarea value={form.papers_refinanced_note || ""} onChange={(e) => update("papers_refinanced_note", e.target.value)} placeholder="Zusätzliche Vermerke …" rows={2} className="w-full rounded-sm border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827]" data-testid="form-papers-refinanced-note" />
                                    </div>
                                )}
                                {form.papers_shipped_to_buyer && (
                                    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-sm p-2">
                                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">Versanddatum <InfoHint id="dispo.papers_shipped_date" defaultText="Tag, an dem die Fahrzeugpapiere an den Käufer versendet wurden. Wird beim Anhaken eines Empfängers automatisch gesetzt und dient als Nachweis der Übergabe." side="bottom" /></Label>
                                        <Input type="date" value={form.papers_shipped_date || ""} onChange={(e) => update("papers_shipped_date", e.target.value)} className="mt-1 h-8 rounded-sm font-mono-num max-w-[220px]" data-testid="form-papers-shipped-date" />
                                    </div>
                                )}
                            </div>
                            {/* Jun-2026 (User-Wunsch) — Fahrzeugpapierinventur:
                                QR/EAN-Code auf den Papieren scannen. Erster Scan
                                speichert den Code + Zähldatum; ohne Code lässt sich
                                aus der FIN einer erzeugen & als Etikett drucken. */}
                            {listingId && (
                                <div className="pt-2 mt-1 border-t border-gray-100" data-testid="papers-inventory-block">
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <ClipboardCheck className="w-3.5 h-3.5 text-[#FFCC00]" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fahrzeugpapierinventur</span>
                                        <InfoHint id="dispo.papers_inventory" defaultText="Erster Scan speichert den Code & setzt das Zähldatum. Auch über die Fahrzeuginventur-Scan-Seite zählbar. Ist noch kein Code hinterlegt, lässt sich aus der FIN einer erzeugen & als Etikett drucken." />
                                    </div>
                                    {papersCode ? (
                                        <div className="text-[11px] text-gray-600 mb-1.5" data-testid="papers-inventory-status">
                                            Code: <span className="font-mono-num font-bold text-gray-800">{papersCode}</span>
                                            {papersLastAt
                                                ? <> · Letzte Zählung: <strong>{fmtDate(papersLastAt)}</strong></>
                                                : <span className="text-amber-600 font-bold"> · Noch nie gezählt</span>}
                                        </div>
                                    ) : null}
                                    <div className="flex flex-wrap items-end gap-2">
                                        <div className="flex-1 min-w-[180px]">
                                            <Input
                                                value={papersScan}
                                                onChange={(e) => setPapersScan(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanPapers(); } }}
                                                placeholder="QR/EAN-Code der Papiere scannen / eingeben"
                                                className="h-8 rounded-sm font-mono-num"
                                                data-testid="papers-inventory-scan-input"
                                            />
                                        </div>
                                        <Button type="button" onClick={scanPapers} disabled={papersBusy} className="h-8 rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="papers-inventory-scan-btn">
                                            {papersBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-3.5 h-3.5 mr-1.5" />{papersCode ? "Zählen" : "Erfassen"}</>}
                                        </Button>
                                        {!papersCode ? (
                                            <Button type="button" variant="outline" onClick={generatePapers} disabled={papersBusy || !(form.vin || "").trim()} className="h-8 rounded-sm" data-testid="papers-inventory-generate-btn">
                                                <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Code aus FIN erstellen & drucken
                                            </Button>
                                        ) : (
                                            <Button type="button" variant="outline" onClick={openPapersLabel} disabled={papersBusy} className="h-8 rounded-sm" data-testid="papers-inventory-print-btn">
                                                <Printer className="w-3.5 h-3.5 mr-1.5" /> Etikett drucken
                                            </Button>
                                        )}
                                    </div>
                                    {!papersCode && !(form.vin || "").trim() && (
                                        <p className="mt-1 text-[10px] text-amber-600 font-bold" data-testid="papers-inventory-vin-required">Für die Code-Erstellung zuerst die vollständige Fahrgestellnummer (FIN) oben erfassen.</p>
                                    )}
                                </div>
                            )}

                            {/* Feb-2026 (User-Wunsch) — „Anzahl der Schlüssel" und
                                „Schlüsseldepot / Leitzahl" in EINER Zeile (Leitzahl
                                persistiert im Feld `leitzahl`). */}
                            <div className="flex items-end gap-3 pt-1">
                                <div className="shrink-0">
                                    <Label className="text-xs font-bold text-gray-600 block mb-1">Anzahl der Schlüssel</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="20"
                                        value={form.key_count ?? ""}
                                        onChange={(e) => update("key_count", e.target.value)}
                                        placeholder="z. B. 2"
                                        className="h-8 max-w-[90px] rounded-sm font-mono-num"
                                        data-testid="erhalten-key-count"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Label className="text-xs font-bold text-gray-600 mb-1 inline-flex items-center gap-1.5">Schlüsseldepot <InfoHint id="dispo.key_depot" defaultText={"Ablageort/Kennung des Schlüssels (z. B. „GW MUC 48“ oder „47FW“). Wird beim Fahrzeug-Excel-Import aus der Spalte „Schlüsseldepot“ übernommen; „Auto“ erzeugt eine Kennung aus den letzten FIN-Ziffern + dem Straßennamen."} /></Label>
                                    <div className="flex items-center gap-1.5">
                                        <Input
                                            value={form.key_depot || ""}
                                            onChange={(e) => update("key_depot", e.target.value.slice(0, 200))}
                                            placeholder="z. B. GW MUC 48"
                                            className="h-8 rounded-sm"
                                            data-testid="form-key-depot"
                                        />
                                        <Button type="button" variant="outline" onClick={fillLeitzahl} className="h-8 rounded-sm px-2.5" title="Schlüsseldepot automatisch aus FIN + Straße erzeugen" data-testid="form-leitzahl-auto-btn">
                                            <Wand2 className="w-3.5 h-3.5 mr-1" /> Auto
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="pl-4 border-l-2 border-gray-100 ml-1 space-y-1.5 pt-1">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input type="checkbox" checked={!!form.has_immobilizer_card} onChange={(e) => update("has_immobilizer_card", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="erhalten-immobilizer" />
                                    Wegfahrsperren / Masterschlüssel
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input type="checkbox" checked={!!form.has_keyless_transponder} onChange={(e) => update("has_keyless_transponder", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="erhalten-keyless" />
                                    KeylessGo Sicherheitskarte / Transponder
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input type="checkbox" checked={!!form.has_handsender} onChange={(e) => update("has_handsender", e.target.checked)} className="w-4 h-4 accent-[#111827]" data-testid="erhalten-fernbedienung" />
                                    Fernbedienung
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Feb-2026 (User-Wunsch) — Standort/Lagerort direkt unter den
                        Fahrzeug-QR-Code (rechte Spalte) verschoben. */}
                    <div className="space-y-3" data-testid="disposition-location-block">
                        {listingId && (
                            <DispoLocationNote
                                listingId={listingId}
                                dispoLocation={form.dispo_location}
                                ownAddress={{ location: form.location, street: form.street, postal_code: form.postal_code, stand_platz: form.stand_platz }}
                                onSaved={(loc) => update("dispo_location", loc || null)}
                            />
                        )}
                        <div><Label className="text-xs font-bold uppercase text-gray-500">Stand-Platz</Label><Input value={form.stand_platz || ""} onChange={(e) => update("stand_platz", e.target.value)} placeholder="z. B. Halle 2, Box 14" className="mt-1.5 rounded-sm" data-testid="form-stand-platz" /></div>
                    </div>
                </div>
            </div>

            {/* Feb-2026 (User-Wunsch) — QR-Code + Standort in die rechte Spalte verschoben (siehe oben). */}

            {/* Feb-2026 (User-Wunsch 27.05.2026) — VIN ist nach OBEN (über den
                Zulassungsstatus) in die VIN-Bereich-Zeile verschoben. */}

            {/* Juni-2026 (User-Wunsch) — Sektion „Zuständiger Verkäufer" entfernt
                (wird nicht mehr benötigt). */}

            {/* Feb-2026 (User-Wunsch) — Portal-Transaktion-Hinweistext entfernt. */}


            {/* Phase 4.19 — Disposition-Aktionen (nur Edit-Mode, da listingId benötigt).
                Jul-2026: bei hideActions in eigener Sektion „Verkaufsabwicklung" gerendert. */}
            {!hideActions && listingId && <DispositionActions listingId={listingId} condition={form.condition} />}
        </div>
    );
}
